import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * Link-to-Jira modal controller (CONEK-7204).
 *
 * data-controller="support--jira-link"
 * data-support--jira-link-projects-url-value="/support/integrations/jira/projects"
 * data-support--jira-link-issue-search-url-value="/support/integrations/jira/tickets/:id/issue_search"
 * data-support--jira-link-linked-to-label-value="Linked to %{ticket}"
 * data-support--jira-link-no-issues-label-value="No issues found."
 * data-support--jira-link-load-error-label-value="Could not load Jira issues."
 *
 * Step 0 offers the disjunctive choice; from there the controller runs one of
 * two flows that share the project picker:
 *
 *   Create (unchanged wizard):
 *     1. Pick a Jira project (searchable, loaded from the projects endpoint)
 *     2. Choose an issue type + edit the auto-filled summary
 *     3. Submit — native form POST to create_issue, which redirects back
 *
 *   Link (new flow):
 *     1. Same project picker
 *     2. Free-text issue search (debounced like the project search) over the
 *        issue_search endpoint, "Load more" pagination, rows carrying key /
 *        summary / type / status and a "Linked to TK-xxxx" pill when the
 *        server annotates a cross-ticket link
 *     3. Clicking a row submits the link form natively (the row is a
 *        type=submit button whose name/value posts `external_id`), so the
 *        server responds with flash + redirect and the sidebar refreshes
 *
 * Targets:
 *   modal, stepChoice, createChoiceBtn,
 *   stepProject, projectSearch, projects, projectsLoading,
 *   form, stepConfigure, selectedProject, issueType, summary, description,
 *   error, projectKeyField, issueTypeField, summaryField, confirmBtn, confirmLabel,
 *   linkForm, stepLink, linkSelectedProject, issueSearch, issues, issuesLoading, loadMoreBtn
 */
export default class extends Controller {
  static targets = [
    "modal",
    "stepChoice",
    "createChoiceBtn",
    "stepProject",
    "projectSearch",
    "projects",
    "projectsLoading",
    "form",
    "stepConfigure",
    "selectedProject",
    "issueType",
    "summary",
    "description",
    "error",
    "projectKeyField",
    "issueTypeField",
    "summaryField",
    "confirmBtn",
    "confirmLabel",
    "linkForm",
    "stepLink",
    "linkSelectedProject",
    "issueSearch",
    "issues",
    "issuesLoading",
    "loadMoreBtn",
  ]

  static values = {
    projectsUrl: String, // e.g. /s:group:branch/support/integrations/jira/projects
    issueSearchUrl: String, // .../jira/tickets/:id/issue_search
    linkedToLabel: String, // "Linked to %{ticket}"
    noIssuesLabel: String,
    loadErrorLabel: String,
  }

  connect() {
    this._searchTimeout = null
    this._issueSearchTimeout = null
    this._mode = null // null (choice) | "create" | "link"
    this._selectedProjectKey = null
    this._selectedProjectName = null
    this._issueQuery = ""
    this._nextCursor = null
    this._defaultSummary = this.hasSummaryTarget ? this.summaryTarget.value : ""
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    this.resetState()
    this._loadProjects()
    requestAnimationFrame(() => {
      this.createChoiceBtnTarget.focus()
      this._refreshLexxyEditor()
    })
  }

  // The <lexxy-editor> is rendered inside a hidden parent on first paint, which
  // can leave it un-upgraded. Force a disconnect/reconnect cycle when the modal
  // opens — but only if Lexxy hasn't already mounted its contenteditable.
  // Mirrors the global reinitializeLexxyEditors() in application.js, scoped
  // to this modal so we don't poke unrelated editors.
  _refreshLexxyEditor() {
    if (!this.hasDescriptionTarget) return

    const editor = this.descriptionTarget
    if (
      editor.querySelector("[contenteditable]") ||
      editor.querySelector("[role='textbox']") ||
      editor.querySelector(".lexxy-editor__content")
    ) {
      return
    }

    const parent = editor.parentNode
    if (!parent) return

    const next = editor.nextSibling
    parent.removeChild(editor)
    parent.insertBefore(editor, next)
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.resetState()
  }

  // Step 0 — the two buttons. The choice is remembered so the shared project
  // picker knows which flow to advance into on selection.
  chooseCreate() {
    this._mode = "create"
    this.stepChoiceTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")
    this.stepProjectTarget.classList.remove("hidden")
    requestAnimationFrame(() => this.projectSearchTarget.focus())
    this._refreshLexxyEditor()
  }

  chooseLink() {
    this._mode = "link"
    this.stepChoiceTarget.classList.add("hidden")
    this.linkFormTarget.classList.remove("hidden")
    this.stepProjectTarget.classList.remove("hidden")
    requestAnimationFrame(() => this.projectSearchTarget.focus())
  }

  resetState() {
    this._mode = null
    this._selectedProjectKey = null
    this._selectedProjectName = null
    this._issueQuery = ""
    this._nextCursor = null

    this.stepChoiceTarget.classList.remove("hidden")
    this.stepProjectTarget.classList.add("hidden")
    this.formTarget.classList.add("hidden")
    this.linkFormTarget.classList.add("hidden")
    this.stepConfigureTarget.classList.add("hidden")
    this.stepLinkTarget.classList.add("hidden")

    this.projectSearchTarget.value = ""
    this.projectsTarget.innerHTML = ""
    this.projectsLoadingTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    this.summaryTarget.value = this._defaultSummary
    this.confirmBtnTarget.disabled = true
    this.projectKeyFieldTarget.value = ""
    this.issueTypeFieldTarget.value = ""
    this.summaryFieldTarget.value = ""

    this.issueSearchTarget.value = ""
    this.issuesTarget.innerHTML = ""
    this.issuesLoadingTarget.classList.add("hidden")
    this.loadMoreBtnTarget.classList.add("hidden")
  }

  searchProjects() {
    if (this._searchTimeout) clearTimeout(this._searchTimeout)
    const query = this.projectSearchTarget.value.trim()
    this._searchTimeout = setTimeout(() => this._loadProjects(query), 300)
  }

  async _loadProjects(query = "") {
    this.projectsLoadingTarget.classList.remove("hidden")
    this.projectsTarget.innerHTML = ""

    try {
      const url = query
        ? `${this.projectsUrlValue}?q=${encodeURIComponent(query)}`
        : this.projectsUrlValue
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) {
        const data = await response.json.catch(() => ({}))
        // eslint-disable-next-line conek-i18n/no-server-error-fallback -- Jira endpoints localize error strings server-side (I18n.t in JiraController), the rule's documented exception.
        throw new Error(data.error || "Could not load projects")
      }

      const projects = await response.json
      this._renderProjects(projects)
    } catch (e) {
      // Error message could contain attacker-controlled text from the Jira
      // API response — use textContent (DOM) instead of HTML interpolation.
      this.projectsTarget.textContent = ""
      const errEl = document.createElement("p")
      errEl.className = "text-xs text-danger py-2"
      errEl.textContent = e.message || "Could not load Jira projects."
      this.projectsTarget.appendChild(errEl)
    } finally {
      this.projectsLoadingTarget.classList.add("hidden")
    }
  }

  _renderProjects(projects) {
    // Build with DOM APIs (createElement + dataset + textContent) instead of
    // string interpolation so attacker-controlled project metadata — Jira
    // project names allow quotes and any printable chars — can never break
    // out of an HTML attribute. dataset.x and .textContent both sanitise
    // automatically; <img src> uses the URL sanitizer to block javascript:.
    this.projectsTarget.textContent = ""

    if (!projects.length) {
      const empty = document.createElement("p")
      empty.className = "text-xs text-muted-foreground py-2"
      empty.textContent = "No Jira projects found."
      this.projectsTarget.appendChild(empty)
      return
    }

    const wrapper = document.createElement("div")
    wrapper.className = "divide-y divide-border/50"

    for (const p of projects) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className =
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 transition-colors text-left group"
      btn.dataset.action = "click->support--jira-link#selectProject"
      btn.dataset.projectKey = p.key || ""
      btn.dataset.projectName = p.name || ""

      const safeAvatar = this._safeImageUrl(p.avatar)
      if (safeAvatar) {
        const img = document.createElement("img")
        img.src = safeAvatar
        img.alt = ""
        img.className = "w-5 h-5 rounded shrink-0"
        btn.appendChild(img)
      }

      const meta = document.createElement("div")
      meta.className = "flex-1 min-w-0"

      const nameEl = document.createElement("p")
      nameEl.className =
        "text-sm text-foreground group-hover:text-primary transition-colors truncate"
      nameEl.textContent = p.name || ""
      meta.appendChild(nameEl)

      const keyEl = document.createElement("p")
      keyEl.className = "text-[11px] font-mono text-muted-foreground"
      keyEl.textContent = p.key || ""
      meta.appendChild(keyEl)

      btn.appendChild(meta)
      wrapper.appendChild(btn)
    }

    this.projectsTarget.appendChild(wrapper)
  }

  // Allow http(s) URLs only — protects against javascript: / data: URLs in
  // <img src>. Atlassian avatar URLs are always HTTPS CDN paths in practice,
  // but a misconfigured proxy or compromised connector could surface a
  // hostile URL; cheap to defend.
  _safeImageUrl(url) {
    if (!url) return null
    try {
      const parsed = new URL(url, window.location.href)
      return parsed.protocol === "https:" || parsed.protocol === "http:"
        ? parsed.href
        : null
    } catch {
      return null
    }
  }

  async selectProject(event) {
    const btn = event.currentTarget
    const key = btn.dataset.projectKey
    const name = btn.dataset.projectName

    this._selectedProjectKey = key
    this._selectedProjectName = name
    this.stepProjectTarget.classList.add("hidden")

    if (this._mode === "link") {
      this.linkSelectedProjectTarget.textContent = `${name} (${key})`
      this.stepLinkTarget.classList.remove("hidden")
      this.issueSearchTarget.value = ""
      this._issueQuery = ""
      this._nextCursor = null
      requestAnimationFrame(() => this.issueSearchTarget.focus())
      await this._loadIssues("", null)
      return
    }

    // Create flow — untouched wizard behaviour.
    this.projectKeyFieldTarget.value = key
    this.selectedProjectTarget.textContent = `${name} (${key})`

    // Advance to step 2
    this.stepConfigureTarget.classList.remove("hidden")
    this.confirmBtnTarget.disabled = false

    await this._loadIssueTypes(key)
  }

  backToProjects() {
    this.stepConfigureTarget.classList.add("hidden")
    this.stepLinkTarget.classList.add("hidden")
    this.formTarget.classList.add("hidden")
    this.linkFormTarget.classList.add("hidden")
    this.confirmBtnTarget.disabled = true
    this.stepProjectTarget.classList.remove("hidden")
    this._selectedProjectKey = null
    this._selectedProjectName = null
    requestAnimationFrame(() => this.projectSearchTarget.focus())
  }

  // ── Link flow: issue picker ────────────────────────────────

  // Debounced on the same cadence as searchProjects (300ms) so both pickers
  // feel identical; every keystroke restarts the timer and replaces the list.
  searchIssues() {
    if (this._issueSearchTimeout) clearTimeout(this._issueSearchTimeout)
    const query = this.issueSearchTarget.value.trim()
    this._issueSearchTimeout = setTimeout(
      () => this._loadIssues(query, null),
      300,
    )
  }

  loadMore() {
    if (!this._nextCursor) return
    // A pending debounced search would overwrite the appended page the
    // moment it fires; clicking Load more supersedes it.
    if (this._issueSearchTimeout) clearTimeout(this._issueSearchTimeout)
    this._loadIssues(this._issueQuery, this._nextCursor)
  }

  // Enter inside the search input would trigger an implicit form submission
  // through the FIRST row button (a type=submit carrying an external_id) —
  // linking an arbitrary issue. Swallow the key; rows are clicked, not typed.
  preventEnter(event) {
    event.preventDefault()
  }

  async _loadIssues(query, cursor) {
    // stepLink only mounts after a project selection, so this is a fail-fast
    // guard, not a reachable state: an empty project_key would search every
    // Jira project instead of failing loudly.
    if (!this._selectedProjectKey) return
    if (cursor === null) {
      // Fresh search: replace the list and clear pagination state.
      this._issueQuery = query
      this._nextCursor = null
      this.issuesTarget.innerHTML = ""
    }
    this.issuesLoadingTarget.classList.remove("hidden")

    try {
      const params = new URLSearchParams()
      params.set("project_key", this._selectedProjectKey)
      if (query) params.set("q", query)
      if (cursor) params.set("cursor", cursor)
      const url = `${this.issueSearchUrlValue}?${params.toString()}`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) {
        const data = await response.json.catch(() => ({}))
        // eslint-disable-next-line conek-i18n/no-server-error-fallback -- Jira endpoints localize error strings server-side (I18n.t in JiraController), the rule's documented exception.
        throw new Error(data.error || this.loadErrorLabelValue)
      }

      const data = await response.json
      this._renderIssues(data.issues || [], cursor !== null)
      this._nextCursor = data.next_cursor || null
      this.loadMoreBtnTarget.classList.toggle("hidden", !this._nextCursor)
    } catch (e) {
      // Error message could contain attacker-controlled text from the Jira
      // API response — use textContent (DOM) instead of HTML interpolation.
      this.issuesTarget.querySelector("[data-issue-load-error]")?.remove()
      const errEl = document.createElement("p")
      errEl.className = "text-xs text-danger py-2"
      errEl.dataset.issueLoadError = ""
      errEl.textContent = e.message || this.loadErrorLabelValue
      if (cursor === null) {
        // Fresh search failure: replace whatever was listed before.
        this.issuesTarget.textContent = ""
      }
      // Pagination failure: keep the already-loaded pages on screen.
      this.issuesTarget.appendChild(errEl)
      this._nextCursor = null
      this.loadMoreBtnTarget.classList.add("hidden")
    } finally {
      this.issuesLoadingTarget.classList.add("hidden")
    }
  }

  _renderIssues(issues, append) {
    if (!append) this.issuesTarget.textContent = ""

    if (!issues.length && !append) {
      const empty = document.createElement("p")
      empty.className = "text-xs text-muted-foreground py-2"
      empty.textContent = this.noIssuesLabelValue
      this.issuesTarget.appendChild(empty)
      return
    }

    const wrapper = this.issuesTarget.querySelector("[data-issue-list]")
    if (wrapper) {
      // Keep the existing list element when appending so load-more doesn't
      // re-render (and re-key) the first page's rows.
      this._appendIssueRows(wrapper, issues)
      return
    }

    const list = document.createElement("div")
    list.className = "divide-y divide-border/50"
    list.dataset.issueList = ""
    this._appendIssueRows(list, issues)
    this.issuesTarget.appendChild(list)
  }

  // Rows are type=submit buttons of the link form: the clicked row's
  // name/value posts `external_id` natively, and the server answers with
  // flash + redirect (Turbo navigates _top, so the sidebar refreshes and the
  // localized banner renders). DOM-built like the project rows so Jira
  // summaries (any printable chars) can never break out of markup.
  _appendIssueRows(list, issues) {
    for (const issue of issues) {
      const btn = document.createElement("button")
      btn.type = "submit"
      btn.name = "external_id"
      btn.value = issue.external_id || ""
      btn.className =
        "w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 transition-colors text-left group"

      const meta = document.createElement("div")
      meta.className = "flex-1 min-w-0"

      const summaryEl = document.createElement("p")
      summaryEl.className =
        "text-sm text-foreground group-hover:text-primary transition-colors truncate"
      summaryEl.textContent = issue.summary || ""
      meta.appendChild(summaryEl)

      const facts = document.createElement("p")
      facts.className =
        "text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 truncate"

      const keyEl = document.createElement("span")
      keyEl.className = "font-mono shrink-0"
      keyEl.textContent = issue.external_id || ""
      facts.appendChild(keyEl)

      const typeEl = document.createElement("span")
      typeEl.className = "shrink-0"
      typeEl.textContent = issue.issue_type || ""
      facts.appendChild(typeEl)

      const statusEl = document.createElement("span")
      statusEl.className = "shrink-0 truncate"
      statusEl.textContent = issue.status || ""
      facts.appendChild(statusEl)
      meta.appendChild(facts)

      btn.appendChild(meta)

      if (issue.linked_to_ticket) {
        // Warn-but-allow: the same bug legitimately shares one Jira issue
        // across tickets; the server pre-filtered only THIS ticket's rows.
        const pill = document.createElement("span")
        pill.className =
          "shrink-0 inline-flex items-center px-1.5 py-0.5 mt-0.5 text-[10px] font-medium rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-600"
        pill.textContent = this.linkedToLabelValue.replace(
          "%{ticket}",
          issue.linked_to_ticket,
        )
        btn.appendChild(pill)
      }

      list.appendChild(btn)
    }
  }

  async _loadIssueTypes(projectKey) {
    this._setIssueTypeOptions([{ value: "", label: "Loading…" }])

    try {
      const url = `${this.projectsUrlValue}/${encodeURIComponent(projectKey)}/issue_types`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) throw new Error("Could not load issue types")

      const types = await response.json
      if (!types.length) {
        this._setIssueTypeOptions([{ value: "Task", label: "Task" }])
        return
      }

      this._setIssueTypeOptions(
        types.map((t) => ({ value: t.name, label: t.name })),
      )

      // Default to "Task" if present
      const taskOption = Array.from(this.issueTypeTarget.options).find(
        (o) => o.value.toLowerCase() === "task",
      )
      if (taskOption) this.issueTypeTarget.value = taskOption.value
    } catch {
      this._setIssueTypeOptions([{ value: "Task", label: "Task" }])
    }
  }

  submit(event) {
    // Copy the editable fields into the hidden form inputs before the native POST.
    if (!this._selectedProjectKey) {
      event.preventDefault()
      return
    }
    this.projectKeyFieldTarget.value = this._selectedProjectKey
    this.issueTypeFieldTarget.value = this.issueTypeTarget.value
    this.summaryFieldTarget.value = this.summaryTarget.value.trim()

    // Lock the button to prevent double-submit; native form POST proceeds.
    this.confirmBtnTarget.disabled = true
    this.confirmLabelTarget.textContent = "Creating…"
  }

  // Replaces all options in the issue-type <select> with the given list.
  // `new Option(label, value)` sets text/value via DOM properties, never as
  // HTML strings, so attacker-controlled labels/values can't escape the
  // attribute or inject markup.
  _setIssueTypeOptions(options) {
    this.issueTypeTarget.textContent = ""
    for (const { value, label } of options) {
      this.issueTypeTarget.appendChild(new Option(label, value))
    }
  }
}
